import { apiClient } from "../api/ApiClient";


class ProductService {

    async getProductsByFilter(filters) {
        return apiClient.get(`/api/products?${filters}`);
    }

    async getProductsList(pageSize, currentPage, filters = {}) {
        try {
            const params = new URLSearchParams({
                page: currentPage,
                size: pageSize,
                ...filters
            });

            const response = await apiClient.get(`/api/products/admin/list?${params}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching products list:', error);
            throw error;
        }
    }

    async searchWebSiteProducts(searchQuery) {
        try {
            const response = await apiClient.get('/api/products/admin/search-for-all', {
                params: {
                    search: searchQuery,
                    page: 0,
                    limit: 10
                }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    async getProductFilters(collectionId = 0, categoryId = 0) {
        try {
            const response = await apiClient.get(`/api/products/admin/filters?collectionId=${collectionId}&categoryId=${categoryId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching product filters:', error);
            throw error;
        }
    }

    async getProductDetails(productId) {
        try {
            const response = await apiClient.get(`/api/products/admin/${productId}/details`);
            return response.data;
        } catch (error) {
            console.error('Error fetching product details:', error);
            throw error;
        }
    }

    async getSubCategoriesByCategory(categoryId) {
        try {
            const response = await apiClient.get(`/api/products/admin/sub-categories?categoryId=${categoryId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching subCategories:', error);
            throw error;
        }
    }

    async getCategoriesByCollection(collectionId) {
        try {
            const response = await apiClient.get(`/api/products/admin/categories?collectionId=${collectionId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    }

    async updateProduct(productId, params) {
        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, value]) => value != null)
        );

        try {
            const response = await apiClient.put(`/api/products/admin/${productId}/basic-info`, cleanParams);
            return response.data;
        } catch (error) {
            console.error('Error updating product:', error);
            if (error.response?.data) {
                throw new Error(error.response.data.message || 'Validation failed');
            }
            throw error;
        }
    }

    async updateVariantPrices(productId, params) {
        try {
            const response = await apiClient.put(`/api/products/admin/${productId}/prices`, params);
            return response.data;
        } catch (error) {
            console.error('Error updating product prices:', error);
            if (error.response?.data) {
                throw new Error(error.response.data.message || 'Validation failed');
            }
            throw error;
        }
    }

    async getColors() {
        try {
            const response = await apiClient.get(`/api/products/admin/colors`);
            return response.data;
        } catch (error) {
            console.error('Error fetching active colors:', error);
            throw error;
        }
    }

    async getSizes() {
        try {
            const response = await apiClient.get(`/api/products/admin/sizes`);
            return response.data;
        } catch (error) {
            console.error('Error fetching active sizes:', error);
            throw error;
        }
    }


    async toggleVariantActivation(variantId) {
        try {
            const response = await apiClient.patch(`/api/admin/variants/${variantId}/toggle-active`);
            return response.data;
        } catch (error) {
            console.error('Error toggling variant activation:', error);
            if (error.response?.data) {
                const errorMessage = error.response.data.message ||
                    error.response.data.error ||
                    'Failed to toggle variant activation';
                throw new Error(errorMessage);
            }
            throw error;
        }
    }

    async addVariant(variantData) {
        const processedData = {
            ...variantData,
            productId: Number(variantData.productId),
            colorId: variantData.colorId ? Number(variantData.colorId) : null,
            sizeId: variantData.sizeId ? Number(variantData.sizeId) : null,
            price: parseFloat(variantData.price),
            quantity: parseInt(variantData.quantity),
            withoutBoxQuantity: variantData.withoutBoxQuantity ? parseInt(variantData.withoutBoxQuantity) : 0
        };

        try {
            const response = await apiClient.post('/api/admin/variants', processedData);
            return response.data;
        } catch (error) {
            console.error('Error adding variant:', error);
            if (error.response?.data) {
                const errorMessage = error.response.data.message ||
                    error.response.data.error ||
                    'Failed to add variant';
                throw new Error(errorMessage);
            }
            throw error;
        }
    }

    async toggleProductStatus(productId, statusName) {
        try {
            const response = await apiClient.put(`/api/products/admin/${productId}/status`, statusName);
            return response.data;
        } catch (error) {
            console.error(`Error toggle ${statusName}: `, error);
            throw error;
        }
    }

    async uploadProductImagesForProduct(productId, formData) {
        try {
            const response = await apiClient.post(
                `/api/products/images/${productId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error uploading images:', error);
            throw new Error(error || 'Failed to upload images');
        }
    }

    // async createNewProduct(formData) {
    //     try {
    //         const response = await apiClient.post(
    //             `/api/products/admin`,
    //             formData,
    //             {
    //                 headers: {
    //                     'Content-Type': 'multipart/form-data'
    //                 }
    //             }
    //         );
    //         return response.data;
    //     } catch (error) {
    //         console.error('Error uploading images:', error);
    //         throw error;
    //     }
    // }
    // async createNewProduct(formData) {
    //     try {
    //         const response = await apiClient.post(
    //             `/api/products/admin`,
    //             formData,
    //             {
    //                 headers: {
    //                     'Content-Type': 'multipart/form-data'
    //                 },
    //             }
    //         );

    //         console.log('Product created successfully:', response);

    //         return response;
    //     } catch (error) {
    //         console.log('Error uploading product:', error?.response?.data?.message);
    //         return error?.response?.data
    //     }
    // }

    async createNewProduct(formData) {
        try {

            const response = await apiClient.post(
                '/api/products/admin',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    transformRequest: [(data) => data],
                }
            );

            return response;

        } catch (error) {
            console.error('Full error details:', error);
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.debugMessage ||
                error.message ||
                'Unknown error occurred';

            console.error('Error message:', errorMessage);

            return {
                status: error.response?.status || 500,
                message: errorMessage
            };
        }
    }

    async deleteProductImage(imageId) {
        try {
            const response = await apiClient.delete(`/api/products/images/${imageId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting image:', error);
            throw error;
        }
    }

    async setPrimaryImage(imageId) {
        try {
            await apiClient.put(`/api/products/images/${imageId}/primary`);
        } catch (error) {
            console.error('Error setting primary image:', error);
            throw error;
        }
    }

    updateVariantQuantity = async (variantId, data) => {
        try {
            const response = await apiClient.put(
                `/api/admin/variants/${variantId}/quantity`,
                data
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    toggleProductActivation = async (productId) => {
        try {
            const response = await apiClient.patch(
                `/api/products/admin/${productId}/toggle-active`
            );
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }

}

export default new ProductService();